import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, Row, Tooltip } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'components';
import { DeleteIcon, EditIcon } from 'icons';
import { useTranslation } from 'react-i18next';
import {
  CustomerServiceOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  PlaySquareOutlined,
} from '@ant-design/icons';
import { getPage } from 'hooks/useUser';
import { useTableChangeFilterHook } from 'hooks/useTableChangeFilterHook';
import { useFilter } from 'hooks/useFilter';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { academyArticlesAdapter } from './academyArticles.adapter';
import AcademyArticlesApi from 'libs/api/retailAcademy/academyArticles';
import { fileNameEdit } from 'helpers/FileUtils';
import AcademyCategoriesApi from 'libs/api/retailAcademy/academyCategories';
import moment from 'moment';
import UIHelper from 'helpers/UIHelper';
import ErrorHelper from 'helpers/ErrorHelper';
import LinkWithParams from 'components/linkWithParams';
import { useNavigate } from 'hooks/useNavigate';
import Helpers from '../../../../helpers';
import { selectYears, selectMarcoArea } from '../../contants';

const api = new AcademyArticlesApi();
const academyCategoriesApi = new AcademyCategoriesApi();

export const fileTypes = {
  pdf: 'PDF',
  video: 'VIDEO',
  music: 'MP3',
  word: 'WORD',
  poverpoint: 'POWERPOINT',
  excel: 'EXCEL',
  image: 'IMAGE',
};

const defaultState = {
  academyArticles: [],
  pagination: {
    current: 1,
    pageSize: 30,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
};

const useAcademyArticles = (isMainPage = true, id, isEdit) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(defaultState);
  const [singleData, setSingleData] = useState({
    file: {
      file_name: '',
    },
    type_file: '',
  });
  const [loading, setLoading] = useState(false);
  const onChangeTable = useTableChangeFilterHook();
  const { handleChangeFilters, handleCloseFilter, searchParams } = useFilter();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const [academyCategories, setAcademyCategories] = useState([]);

  const filters = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: shoppingCenters.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Capitoli'),
        type: 'input',
        name: 'by_title',
        values: [],
      },
      {
        title: t('Macro-Area'),
        type: 'select',
        name: 'by_macro_area',
        values: selectMarcoArea.map((i) => ({
          value: i,
          title: i,
        })),
      },
      {
        title: t('Data pubblicazione'),
        type: 'select',
        name: 'by_year',
        values: selectYears.map((i) => ({
          value: i,
          title: i,
        })),
      },
      {
        title: t('Modulo'),
        type: 'select',
        name: 'by_academy_category',
        values: academyCategories.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
    ];
  }, [t, shoppingCenters, academyCategories]);

  const handleDownloadPdf = async (id, fileName) => {
    try {
      const response = await api.getPDF(id);
      Helpers.downloadFile(response, fileName);
    } catch (e) {
      console.log(e);
      UIHelper.serverError();
    }
  };
  const handleDownloadExcel = async () => {
    try {
      const currentParams = Object.fromEntries([...searchParams]);
      const params = {
        ...currentParams,
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }

      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      UIHelper.serverError();
    }
  };

  const columns = useMemo(() => {
    return [
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_centers.title',
        fixed: 'left',
        sorter: () => {},
      },
      {
        title: t('Macro-Area'),
        dataIndex: 'macro_area',
        width: 150,
        sorter: () => {},
      },
      {
        title: t('Modulo'),
        dataIndex: 'academy_categories.title',
        sorter: () => {},
      },
      {
        title: t('Capitoli'),
        dataIndex: 'title',
        sorter: () => {},
      },
      {
        title: t('Data Pubblicazione'),
        dataIndex: 'date',
        sorter: () => {},
        render: (date) => {
          return UIHelper.formatedDate(date);
        },
      },
      {
        title: t('Contenuto'),
        dataIndex: 'active_storage_blobs.content_type',
        width: 120,
        sorter: () => {},
        render: (data, row) => {
          if (!data) return '';
          let icon = <FileOutlined />;
          if (data === fileTypes.pdf) {
            icon = <FilePdfOutlined />;
            return (
              <Button
                onClick={() => handleDownloadPdf(row.id, row?.file?.file_name)}
                type={'green'}
                icon={icon}
              />
            );
          } else if (data === fileTypes.video) {
            icon = <PlaySquareOutlined />;
          } else if (data === fileTypes.music) {
            icon = <CustomerServiceOutlined />;
          }

          return (
            <Link to={`${pathname}/${row.id}`}>
              <Button type={'green'} icon={icon} />
            </Link>
          );
        },
      },
      {
        title: t('Visualizzazioni'),
        dataIndex: 'count_show',
        sorter: () => {},
        width: 160,
      },
      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) => (
          <Row
            gutter={16}
            justify={'center'}
            onClick={(e) => e.stopPropagation()}
          >
            {(pages?.ability?.can_read !== false || row.can_read !== false) && (
              <Col>
                <Tooltip title={t('Visualizza')}>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type='secondary' icon={<EyeOutlined />} />
                  </Link>
                </Tooltip>
              </Col>
            )}
            {row.can_update !== false && (
              <Col>
                <Tooltip title={t('Modifica')}>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Tooltip>
              </Col>
            )}
            {row.can_destroy !== false && (
              <Col>
                <Tooltip title={t('Elimina')}>
                  <LinkWithParams to={`${pathname}/${row.id}/delete`}>
                    <Button type={'red'} icon={<DeleteIcon />} />
                  </LinkWithParams>
                </Tooltip>
              </Col>
            )}
          </Row>
        ),
      },
    ];
  }, [pathname, pages?.ability?.can_read, t]);

  const onChangeTableFilter = useCallback(
    (paginate, sort, filter) => {
      onChangeTable(setData, paginate, sort, filter);
    },
    [onChangeTable],
  );

  const handleCreateOrUpdateShop = async (values, id) => {
    setLoading(true);
    try {
      const formData = new FormData();
      for (let key in values) {
        if (key === 'file') {
          if (values[key]) {
            formData.append(
              `academy_article[${key}]`,
              values[key][0]?.originFileObj || 'null',
            );
          }
        } else if (key === 'in_telegram') {
          formData.append(`academy_article[${key}]`, values[key] || false);
        } else {
          formData.append(`academy_article[${key}]`, values[key] || '');
        }
      }
      if (!id) {
        await api.createArticle(formData);
        navigate(UIHelper.getListRoute(pathname, 1));
      } else {
        await api.updateArticle(id, formData);
        navigate(UIHelper.getListRoute(pathname));
      }
    } catch (e) {
      console.log(e);
      const errors = e.response.data || [];
      form.setFields(
        ErrorHelper.getErrorsForForm(errors, {
          academy_category: 'academy_category_id',
          shopping_center: 'shopping_center_id',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await api.destroy(id);
      await handleGet();
      navigate(UIHelper.getListRoute(pathname));
    } catch (e) {
      const error = e.response.data.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const handleGet = async () => {
    setLoading(true);
    const currentParams = Object.fromEntries([...searchParams]);
    try {
      const params = {
        per: data.pagination.pageSize,
        page: data.pagination.current,
        ...currentParams,
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const {
        data: { academy_articles = [], pagination = { total_count: 0 } },
      } = await api.list({ params });
      setData((s) => ({
        ...s,
        academyArticles: academyArticlesAdapter(academy_articles),
        pagination: {
          ...defaultState.pagination,
          ...pagination,
          total: pagination?.total_count,
        },
      }));
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isMainPage) {
      (async () => {
        await handleGet();
      })();
    }
  }, [data.pagination.current, data.sorting.order, isMainPage, searchParams]);

  useEffect(() => {
    (async () => {
      try {
        const params = {
          per: 10000,
        };
        const {
          data: { academy_categories = [] },
        } = await academyCategoriesApi.list({ params });
        setAcademyCategories(academy_categories);
      } catch (e) {
        console.log(e);
      }
    })();
  }, [isMainPage]);

  useEffect(() => {
    (async () => {
      if (id) {
        setLoading(true);
        try {
          const {
            data: { academy_article },
          } = await api.get(id);
          setSingleData(academy_article);
          if (isEdit) {
            let fieldsValues = [];
            for (let key in academy_article) {
              if (
                key !== 'file' &&
                key !== 'academy_category' &&
                key !== 'shopping_center' &&
                key !== 'date'
              ) {
                fieldsValues.push({
                  name: key,
                  value: academy_article[key],
                });
              } else if (key === 'shopping_center') {
                fieldsValues.push({
                  name: 'shopping_center_id',
                  value: academy_article[key].id,
                });
              } else if (key === 'academy_category') {
                fieldsValues.push({
                  name: 'academy_category_id',
                  value: academy_article[key].id,
                });
              } else if (key === 'date') {
                fieldsValues.push({
                  name: 'date',
                  value: moment(academy_article[key], 'YYYY-MM-DD'),
                });
              } else if (key === 'file') {
                if (academy_article[key]) {
                  let blob = await fetch(academy_article[key].url).then((r) =>
                    r.blob(),
                  );
                  fieldsValues.push({
                    name: 'file',
                    value: [
                      {
                        originFileObj: new File(
                          [blob],
                          academy_article[key].file_name,
                        ),
                        name: academy_article[key].file_name,
                      },
                    ],
                  });
                }
              }
              form.setFields(fieldsValues);
            }
          }
        } catch (e) {
          console.log(e);
        } finally {
          setLoading(false);
        }
      }
    })();
  }, [id]);

  return {
    columns,
    data,
    singleData,
    loading,
    onChangeTableFilter,
    form,
    filters,
    shoppingCenters,
    selectMarcoArea,
    academyCategories,
    fileTypes,
    handleChangeFilters,
    handleCloseFilter,
    handleCreateOrUpdateShop,
    handleDelete,
    handleDownloadExcel,
  };
};

export default useAcademyArticles;
